import { Component, DestroyRef, ElementRef, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import {
  bindFormDirtyTracking,
  clearFormUnsavedChanges,
} from '../../services/unsaved-changes/unsaved-changes-form.util';

const CUSTOMER_FORM_DIRTY_SOURCE = 'customer-form-modal';

import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { ToastrService } from 'ngx-toastr';

import { CommonModule } from '@angular/common';

import { MatButtonModule } from '@angular/material/button';

import { MatFormFieldModule } from '@angular/material/form-field';

import { MatSelectModule } from '@angular/material/select';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
} from 'rxjs';
import {

  CreateCustomerPayload,

  CUSTOMER_TYPE_ID_CUSTOMER,

  CUSTOMER_TYPE_ID_RESELLER,

  CustomerService,

  CustomerStatus,

  CustomerTypeOption,

  CustomerV1ListItem,

  UpdateCustomerPayload,

} from '../../services/customer-service/customer.service';
import { RbacService } from '../../services/rbac/rbac.service';
import { resolveResellerRecordId } from '../../services/rbac/rbac-role.helpers';
import {
  CUSTOMER_CREATE_DUPLICATE_MESSAGE,
  CUSTOMER_CREATE_STATUS_FALLBACKS,
  resolveApiErrorMessage as buildApiErrorMessage,
} from '../../services/api-error-message.util';
import { getBusinessEmailValidators } from '../../services/business-email.validators';
import {
  US_PHONE_NUMBER_INVALID_MESSAGE,
  US_PHONE_NUMBER_PLACEHOLDER,
  getUsPhoneNumberValidators,
} from '../../services/us-phone.validators';
import {
  PERSON_NAME_INVALID_MESSAGE,
  getPersonNameValidators,
  isPersonNameDigitKey,
  sanitizePersonNameInput,
} from '../../services/person-name.validators';
import {
  getFormFieldErrorMessage,
  isFormFieldInvalid,
} from '../../services/form-field-error.util';
import {
  DEFAULT_USA_COUNTRY,
  USA_COUNTRIES,
  US_STATES,
  getUsaCitiesForState,
  isUsaCountry,
  normalizeUsaCountry,
  normalizeUsaState,
  withExistingCityOption,
} from '../../services/usa-location.data';

/** Tokens allowed in customer Wiliot gateway naming format. */
const WILIOT_GATEWAY_TOKEN_SCAN = /%[^%]+%/g;
const WILIOT_GATEWAY_SUPPORTED_TOKEN =
  /^%(?:AssetID|GWID)(?::(?:-?\d+)(?:,\d+)?)?%$|^%Group%$/i;

export function wiliotGatewayNameFormatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = String(control.value ?? '').trim();
    if (!raw) {
      return null;
    }
    if (raw.length > 255) {
      return { wiliotGatewayNameFormatMaxLength: true };
    }
    const tokens = raw.match(WILIOT_GATEWAY_TOKEN_SCAN) ?? [];
    for (const token of tokens) {
      if (!WILIOT_GATEWAY_SUPPORTED_TOKEN.test(token)) {
        return { wiliotGatewayNameFormatToken: { token } };
      }
    }
    return null;
  };
}

export interface CustomerDialogResult {
  success: true;
}

export interface CustomerDialogData {

  /** 'add' opens a blank form and calls createCustomer()

   *  'edit' pre-fills the form and calls updateCustomerOnly() */

  mode: 'add' | 'edit';

  rowData?: any;

}



@Component({

  selector: 'app-edit-customer-list',

  standalone: true,

  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatDialogModule, MatSelectModule, MatProgressSpinnerModule],

  templateUrl: './edit-customer-list.component.html',

  styleUrl: './edit-customer-list.component.scss'

})

export class EditCustomerListComponent implements OnInit, OnDestroy {
  private static readonly CREATE_CUSTOMER_FAILED_TITLE = 'Failed to Create Customer';

  private static readonly MONTAGE_PARENT_ORGANIZATION_NAME = 'Montage';

  /** Display labels for generic required/invalid messages (any validated control). */
  private static readonly FIELD_LABELS: Readonly<Record<string, string>> = {
    contactFirstName: 'First Name',
    contactLastName: 'Last Name',
    country: 'Country',
    state: 'State',
    city: 'City',
    zip: 'Zip Code',
    address: 'Address',
    phoneNumber: 'Phone Number',
    customerName: 'Organization Name',
    accountNumber: 'Account Number',
    contactEmail: 'Contact Email',
    customerTypeId: 'Organization Type',
    parentResellerId: 'Parent Organization',
    wiliotGatewayNameFormat: 'Wiliot Gateway Naming Format',
  };

  readonly wiliotGatewayTokenHelp = [
    { token: '%GWID%', description: 'Gateway ID' },
    { token: '%AssetID%', description: 'Asset ID' },
    { token: '%Group%', description: 'Customer Group Name' },
  ] as const;

  showWiliotGatewayTokenPopover = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly rbacService = inject(RbacService);
  private readonly unsavedChanges = inject(UnsavedChangesTrackerService);

  customerListForm!: FormGroup;

  isSubmitting = false;

  isLoadingCustomer = false;

  /**
   * Non-blocking informative message when account number already exists for another customer.
   * Must never disable Save/Update (AC4).
   */
  accountNumberInfoMessage = '';

  /** Account number loaded for edit — duplicate check skips when unchanged. */
  private originalAccountNumber = '';

  private accountNumberListenerBound = false;

  customerTypes: CustomerTypeOption[] = [];

  resellers: CustomerV1ListItem[] = [];

  isLoadingResellers = false;

  /** Display label when Parent Customer is locked for reseller users. */
  lockedParentResellerLabel = '';

  /** Resolved parent reseller id used for reseller-scoped customer create. */
  private lockedParentResellerId: number | null = null;

  isResellerParentReady = false;

  /** Internal record id for GET/PUT — captured from grid row or API response. */
  private editRecordId: number | string | null = null;

  /** Resolved Montage parent id when customer type is Reseller. */
  private montageParentOrganizationId: number | null = null;

  private montageParentDisplayLabel = '';

  get isResellerParentLoading(): boolean {
    return this.isResellerCustomerLocked && this.isAddMode && this.isLoadingResellers;
  }

  get isSubmitBlockedForResellerParent(): boolean {
    return (
      this.isResellerCustomerLocked &&
      this.isAddMode &&
      this.isCustomerTypeId(this.customerListForm?.get('customerTypeId')?.value) &&
      !this.isResellerParentReady
    );
  }

  /** Submit stays disabled until every validated control is valid. */
  get isSubmitDisabled(): boolean {
    return (
      !this.customerListForm ||
      this.customerListForm.invalid ||
      this.isSubmitting ||
      this.isSubmitBlockedForResellerParent
    );
  }

  readonly usPhoneNumberPlaceholder = US_PHONE_NUMBER_PLACEHOLDER;
  readonly usPhoneNumberInvalidMessage = US_PHONE_NUMBER_INVALID_MESSAGE;
  readonly personNameInvalidMessage = PERSON_NAME_INVALID_MESSAGE;
  readonly usaCountries = USA_COUNTRIES;
  readonly usaStates = US_STATES;
  availableCities: string[] = [];
  private locationListenersBound = false;

  get isUsaCountrySelected(): boolean {
    return isUsaCountry(this.customerListForm?.get('country')?.value);
  }

  get isStateSelected(): boolean {
    return !!this.customerListForm?.get('state')?.value;
  }

  get isAddMode(): boolean { return this.data?.mode === 'add'; }

  get isEditMode(): boolean { return this.data?.mode === 'edit'; }

  /** Reseller Admin / Reseller User — customer type and parent customer are role-locked. */
  get isResellerCustomerLocked(): boolean {
    return this.rbacService.isResellerRole();
  }

  get visibleCustomerTypes(): CustomerTypeOption[] {
    if (!this.isResellerCustomerLocked) {
      return this.customerTypes;
    }
    if (this.isEditMode) {
      const currentTypeId = this.resolveCurrentCustomerTypeId();
      if (currentTypeId != null) {
        const recordType = this.customerTypes.filter(
          (type) => Number(type.typeId) === currentTypeId
        );
        if (recordType.length) {
          return recordType;
        }
      }
    }
    return this.customerTypes.filter((type) => this.isCustomerTypeId(type.typeId));
  }

  get showParentCustomer(): boolean {
    const typeId = this.resolveCurrentCustomerTypeId();
    if (this.isResellerCustomerLocked && this.isAddMode) {
      return true;
    }
    return this.isCustomerTypeId(typeId) || this.isResellerTypeId(typeId);
  }

  /** Parent Organization is read-only for Reseller type (Montage) or reseller-role users. */
  get isParentOrganizationLocked(): boolean {
    return this.isResellerCustomerLocked || this.isMontageParentLocked;
  }

  get isMontageParentLocked(): boolean {
    return this.isResellerTypeId(this.resolveCurrentCustomerTypeId());
  }

  get lockedParentOrganizationDisplayLabel(): string {
    if (this.isMontageParentLocked) {
      return (
        this.montageParentDisplayLabel ||
        EditCustomerListComponent.MONTAGE_PARENT_ORGANIZATION_NAME
      );
    }
    return this.lockedParentResellerLabel || 'Loading...';
  }

  resolveResellerId(reseller: CustomerV1ListItem): number {
    return resolveResellerRecordId(reseller) ?? 0;
  }

  /**
   * True when a control should show invalid UI (blur/submit marked it touched).
   * Works for any control that has validators — no per-field branching.
   */
  isFieldInvalid(controlName: string): boolean {
    return isFormFieldInvalid(this.customerListForm, controlName);
  }

  /**
   * First applicable inline error for a touched invalid control.
   * Add a FIELD_LABELS entry (and validators) for new required fields — template stays the same pattern.
   */
  getFieldErrorMessage(controlName: string): string | null {
    const ctrl = this.customerListForm?.get(controlName);
    if (ctrl?.touched && ctrl.errors?.['wiliotGatewayNameFormatMaxLength']) {
      return 'Wiliot Gateway Naming Format must not exceed 255 characters.';
    }
    if (ctrl?.touched && ctrl.errors?.['wiliotGatewayNameFormatToken']) {
      const token = ctrl.errors['wiliotGatewayNameFormatToken']?.token;
      return token
        ? `Unsupported token '${token}'. Supported tokens: %GWID%, %AssetID%, %Group%.`
        : 'Unsupported token in Wiliot Gateway Naming Format.';
    }
    return getFormFieldErrorMessage(
      this.customerListForm,
      controlName,
      EditCustomerListComponent.FIELD_LABELS,
      {
        personNameInvalidMessage: this.personNameInvalidMessage,
        patternMessages: {
          phoneNumber: this.usPhoneNumberInvalidMessage,
        },
      }
    );
  }

  /** Prevent digit entry in person-name fields (First / Last Name). */
  onPersonNameKeydown(event: KeyboardEvent): void {
    if (isPersonNameDigitKey(event)) {
      event.preventDefault();
    }
  }

  /** Strip digits after paste / autofill into person-name fields. */
  onPersonNameInput(controlName: string, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const sanitized = sanitizePersonNameInput(input.value);
    if (sanitized === input.value) {
      return;
    }
    input.value = sanitized;
    this.customerListForm.get(controlName)?.setValue(sanitized, { emitEvent: true });
  }

  constructor(

    private dialogRef: MatDialogRef<EditCustomerListComponent>,

    private fb: FormBuilder,

    private readonly toastr: ToastrService,

    private customerService: CustomerService,

    @Inject(MAT_DIALOG_DATA) public data: CustomerDialogData

  ) {}



  ngOnInit(): void {
    // Initialize form immediately so template [formGroup] is never undefined (NG01052).
    this.buildForm({});
    bindFormDirtyTracking(
      this.customerListForm,
      CUSTOMER_FORM_DIRTY_SOURCE,
      this.unsavedChanges,
      this.destroyRef
    );
    clearFormUnsavedChanges(
      this.customerListForm,
      CUSTOMER_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );

    this.rbacService.ensureContextLoaded().subscribe(() => {
      this.loadCustomerTypes();
    });
  }

  ngOnDestroy(): void {
    this.unsavedChanges.clear(CUSTOMER_FORM_DIRTY_SOURCE);
  }

  private loadCustomerTypes(): void {
    this.customerService.getCustomerTypes().subscribe({
      next: (types) => {
        this.customerTypes = this.sortCustomerTypes(types);

        this.bindCustomerTypeChanges();

        if (!this.isEditMode) {
          if (this.isResellerCustomerLocked) {
            this.applyResellerCustomerRules();
          } else {
            const defaultTypeId = this.getDefaultCustomerTypeId();
            if (defaultTypeId != null) {
              this.customerListForm.patchValue({ customerTypeId: defaultTypeId });
              this.updateParentCustomerValidators(defaultTypeId);
              if (this.isCustomerTypeId(defaultTypeId)) {
                this.loadResellers();
              }
            }
          }
          clearFormUnsavedChanges(
            this.customerListForm,
            CUSTOMER_FORM_DIRTY_SOURCE,
            this.unsavedChanges
          );
        } else {
          this.loadCustomerForEdit();
        }
      },
      error: () => {
        this.toastr.warning(
          'Failed to load customer types.',
          'Load Warning',
          { timeOut: 5000, closeButton: true, progressBar: true }
        );
        if (this.isEditMode) {
          this.loadCustomerForEdit();
        }
      },
    });
  }



  private loadCustomerForEdit(): void {
    const fallback = (this.data?.rowData ?? {}) as Record<string, unknown>;
    const recordId = this.resolveCustomerRecordId(fallback);
    this.editRecordId = recordId;

    if (recordId == null) {
      this.patchFormFromCustomer(fallback);
      return;
    }

    this.isLoadingCustomer = true;
    this.customerService.getCustomerById(recordId).subscribe({
      next: (response: any) => {
        const row = this.normalizeCustomerRecord(response);
        const resolvedId = this.resolveCustomerRecordId(row);
        if (resolvedId != null) {
          this.editRecordId = resolvedId;
        }
        this.patchFormFromCustomer(row, fallback);
        this.isLoadingCustomer = false;
      },
      error: () => {
        this.patchFormFromCustomer(fallback);
        this.isLoadingCustomer = false;
      },
    });
  }

  /** GET/PUT /customers/v1/customer/{id} requires the internal record id, not customerId. */
  private resolveCustomerRecordId(row: Record<string, unknown>): number | string | null {
    const raw = row['id'];
    if (raw == null || raw === '') {
      return null;
    }
    const numeric = Number(raw);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric;
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }
    return null;
  }

  /** Unwrap GET /customer/{id} and search row shapes. */
  private normalizeCustomerRecord(response: any): any {
    const raw = response?.data ?? response ?? {};
    const merged = { ...raw };
    if (raw?.customer && typeof raw.customer === 'object') {
      Object.assign(merged, raw.customer);
      delete merged.customer;
    }
    return this.flattenCustomerTypeFields(this.flattenUserEmailFields(merged));
  }

  /** Lift contact email fields from current and legacy BE shapes. */
  private flattenUserEmailFields(row: any): any {
    if (!row || typeof row !== 'object') {
      return row ?? {};
    }

    const normalized = { ...row };
    const userRef = normalized.user;
    const contactRef = normalized.contact;
    const primaryUser = normalized.primaryUser ?? normalized.customerUser ?? normalized.adminUser;
    const users = Array.isArray(normalized.users) ? normalized.users : [];
    const firstUser =
      users.find((entry: unknown) => entry != null && typeof entry === 'object') ?? null;

    const candidates = [
      normalized.contactEmail,
      normalized.userEmailId,
      normalized.userEmailID,
      normalized.userEmail,
      normalized.email,
      normalized.emailId,
      normalized.emailAddress,
      normalized.primaryEmail,
      userRef?.userEmailId,
      userRef?.userEmail,
      userRef?.email,
      userRef?.emailId,
      contactRef?.email,
      contactRef?.emailId,
      primaryUser?.userEmailId,
      primaryUser?.userEmail,
      primaryUser?.email,
      primaryUser?.emailId,
      firstUser?.userEmailId,
      firstUser?.userEmail,
      firstUser?.email,
      firstUser?.emailId,
    ];

    for (const value of candidates) {
      if (value != null && String(value).trim() !== '') {
        normalized.contactEmail = String(value).trim();
        break;
      }
    }

    return normalized;
  }

  /** Normalize nested/string customer type shapes from BE into top-level fields. */
  private flattenCustomerTypeFields(row: any): any {
    if (!row || typeof row !== 'object') {
      return row ?? {};
    }

    const normalized = { ...row };
    const typeRef = normalized.customerType;

    if (typeRef && typeof typeRef === 'object') {
      normalized.customerTypeId ??= typeRef.typeId ?? typeRef.id;
      normalized.customerTypeName ??=
        typeRef.label ?? typeRef.name ?? typeRef.typeCode ?? typeRef.typeName;
    } else if (typeof typeRef === 'string' && typeRef.trim()) {
      normalized.customerTypeName ??= typeRef.trim();
    }

    normalized.customerTypeId ??=
      normalized.typeId ?? normalized.customerTypeRefId ?? normalized.customerTypeRef?.typeId;

    return normalized;
  }

  /** Resolve contact email from BE field name variants. */
  private resolveUserEmailId(row: any): string {
    const flat = this.flattenUserEmailFields(row);
    if (!flat) {
      return '';
    }

    const candidates = [
      flat.contactEmail,
      flat.userEmailId,
      flat.userEmailID,
      flat.userEmail,
      flat.email,
      flat.emailId,
      flat.emailAddress,
      flat.primaryEmail,
      flat.user?.userEmailId,
      flat.user?.userEmail,
      flat.user?.email,
      flat.user?.emailId,
      flat.contact?.email,
      flat.contact?.emailId,
    ];

    for (const value of candidates) {
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }

  private patchFormFromCustomer(row: any, fallback?: any): void {
    const normalizedRow = this.flattenCustomerTypeFields(
      this.flattenUserEmailFields(row ?? {})
    );
    const normalizedFallback = this.flattenCustomerTypeFields(
      this.flattenUserEmailFields(fallback ?? {})
    );
    const merged = { ...normalizedFallback, ...normalizedRow };
    const customerTypeId = this.resolveCustomerTypeId(merged);
    const recordId =
      this.editRecordId ??
      this.resolveCustomerRecordId(merged as Record<string, unknown>) ??
      '';
    const contactEmail =
      this.resolveUserEmailId(normalizedRow) || this.resolveUserEmailId(normalizedFallback);
    const country = this.resolveCountryValue(merged);
    const state = this.resolveStateValue(merged);
    const city = merged.city ?? merged.contact?.city ?? '';

    this.customerListForm.patchValue(
      {
        id: recordId,
        customerId: merged.customerId ?? 0,
        customerName: merged.customerName ?? '',
        accountNumber: merged.accountNumber ?? '',
        contactEmail,
        contactFirstName:
          merged.contactFirstName ?? merged.contact?.firstName ?? '',
        contactLastName:
          merged.contactLastName ?? merged.contact?.lastName ?? '',
        city,
        state,
        zip: merged.zip ?? merged.contact?.zip ?? '',
        country,
        address: merged.address ?? merged.contact?.address ?? '',
        customerTypeId,
        cloudProvider: merged.cloudProvider ?? '',
        phoneNumber:
          merged.phoneNumber ??
          merged.contactPhoneNumber ??
          merged.contact?.phoneNumber ??
          merged.phone ??
          '',
        isActive: this.resolveIsActive(merged),
        wiliotEnabled:
          merged.wiliotEnabled ??
          merged.wiliotGatewayFormatEnabled ??
          merged.wiliot_enabled ??
          false,
        wiliotGatewayNameFormat:
          merged.wiliotGatewayNameFormat ??
          merged.wiliot_gateway_name_format ??
          '',
        parentResellerId:
          merged.parentResellerId ??
          merged.parentCustomerId ??
          merged.parentId ??
          merged.resellerId ??
          null,
      },
      { emitEvent: false }
    );

    this.originalAccountNumber = String(merged.accountNumber ?? '').trim();
    this.accountNumberInfoMessage = '';

    this.refreshCityOptions(true);

    this.updateParentCustomerValidators(customerTypeId);
    if (this.isResellerCustomerLocked) {
      if (this.isAddMode) {
        this.applyResellerCustomerRules();
      } else {
        this.applyResellerEditModeRules(customerTypeId);
      }
    } else if (this.isCustomerTypeId(customerTypeId)) {
      this.loadResellers();
    } else if (this.isResellerTypeId(customerTypeId)) {
      this.applyMontageParentOrganizationRules();
    }

    this.applyUserEmailFieldRules();
    this.setCloudValues(merged.cloudProvider || '');
    clearFormUnsavedChanges(
      this.customerListForm,
      CUSTOMER_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
  }



  private buildForm(row: any): void {

    const wiliotValue: boolean =

      row.wiliotEnabled ??

      row.wiliotGatewayFormatEnabled ??

      row.wiliot_enabled ??

      false;



    const emailValue = this.resolveUserEmailId(row);



    const isActive = this.resolveIsActive(row);

    const customerTypeId = this.resolveCustomerTypeId(row);



    this.customerListForm = this.fb.group({

      id:            [{ value: row.id || '', disabled: false }],

      customerId:    [{ value: row.customerId ?? 0, disabled: false }],

      customerName: [row.customerName || '', Validators.required],

      accountNumber: [row.accountNumber || '', Validators.required],

      contactEmail: [
        emailValue,
        getBusinessEmailValidators({ required: true }),
      ],

      contactFirstName: [
        row.contactFirstName ?? row.contact?.firstName ?? '',
        getPersonNameValidators({ required: false, minLength: 2, maxLength: 100 }),
      ],

      contactLastName: [
        row.contactLastName ?? row.contact?.lastName ?? '',
        getPersonNameValidators({ required: false, minLength: 2, maxLength: 100 }),
      ],

      city: [row.city ?? row.contact?.city ?? ''],

      state: [this.resolveStateValue(row)],

      zip: [row.zip ?? row.contact?.zip ?? ''],

      country: [this.resolveCountryValue(row)],

      address: [row.address ?? row.contact?.address ?? ''],

      customerTypeId: [customerTypeId, Validators.required],

      parentResellerId: [
        row.parentResellerId ?? row.parentCustomerId ?? row.parentId ?? null,
      ],

      cloudProvider: [row.cloudProvider || ''],

      phoneNumber: [
        row.phoneNumber ??
          row.contactPhoneNumber ??
          row.contact?.phoneNumber ??
          row.phone ??
          '',
        [...getUsPhoneNumberValidators()],
      ],

      isActive: [
        {
          value: this.isAddMode ? true : isActive,
          disabled: this.isAddMode,
        },
      ],

      wiliotEnabled: [wiliotValue],

      wiliotGatewayNameFormat: [
        row.wiliotGatewayNameFormat ?? row.wiliot_gateway_name_format ?? '',
        [Validators.maxLength(255), wiliotGatewayNameFormatValidator()],
      ],

      wiliotCloud:           [''],

      wiliotEdgeSecurityKey: [row.wiliotEdgeSecurityKey || ''],

      targetUrl:             [''],

    });



    this.setCloudValues(row.cloudProvider || '');



    this.customerListForm
      .get('cloudProvider')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.setCloudValues(value);
      });

    this.applyUserEmailFieldRules();
    this.applyStatusFieldRules();
    this.setupLocationFieldListeners();
    this.setupAccountNumberDuplicateListener();
    this.refreshCityOptions(true);
  }

  /**
   * Calls exists API on account-number changes and shows an informative (non-blocking) message.
   */
  private setupAccountNumberDuplicateListener(): void {
    if (this.accountNumberListenerBound || !this.customerListForm) {
      return;
    }
    this.accountNumberListenerBound = true;

    const control = this.customerListForm.get('accountNumber');
    if (!control) {
      return;
    }

    // Seed original value for edit so the customer's own number does not warn.
    this.originalAccountNumber = String(control.value ?? '').trim();

    control.valueChanges
      .pipe(
        debounceTime(400),
        map((value) => String(value ?? '').trim()),
        distinctUntilChanged(),
        switchMap((accountNumber) => {
          if (!accountNumber) {
            this.accountNumberInfoMessage = '';
            return of(false);
          }
          if (
            this.isEditMode &&
            this.originalAccountNumber &&
            accountNumber === this.originalAccountNumber
          ) {
            this.accountNumberInfoMessage = '';
            return of(false);
          }
          return this.customerService.checkAccountNumberExists(accountNumber).pipe(
            catchError(() => of(false))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((exists) => {
        this.accountNumberInfoMessage = exists
          ? 'This Wiliot Account Number is already associated with another customer.'
          : '';
      });
  }

  /** Add Customer: Status is always Active and not editable. Edit keeps full control. */
  private applyStatusFieldRules(): void {
    const ctrl = this.customerListForm?.get('isActive');
    if (!ctrl) {
      return;
    }
    if (this.isAddMode) {
      ctrl.setValue(true, { emitEvent: false });
      ctrl.disable({ emitEvent: false });
      return;
    }
    ctrl.enable({ emitEvent: false });
  }

  private resolveCountryValue(row: any): string {
    const raw = row?.country ?? row?.contact?.country ?? '';
    const normalized = normalizeUsaCountry(raw);
    if (normalized) {
      return normalized;
    }
    return this.isAddMode ? DEFAULT_USA_COUNTRY : '';
  }

  private resolveStateValue(row: any): string {
    return normalizeUsaState(row?.state ?? row?.contact?.state ?? '');
  }

  private setupLocationFieldListeners(): void {
    if (this.locationListenersBound || !this.customerListForm) {
      return;
    }
    this.locationListenersBound = true;

    this.customerListForm
      .get('country')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isUsaCountrySelected) {
          this.customerListForm.patchValue(
            { state: '', city: '' },
            { emitEvent: false }
          );
        }
        this.refreshCityOptions();
      });

    this.customerListForm
      .get('state')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.customerListForm.patchValue({ city: '' }, { emitEvent: false });
        this.refreshCityOptions();
      });
  }

  private refreshCityOptions(preserveCurrentCity = false): void {
    if (!this.customerListForm || !this.isUsaCountrySelected) {
      this.availableCities = [];
      this.updateLocationFieldStates();
      return;
    }

    const stateValue = this.customerListForm.get('state')?.value;
    const currentCity = preserveCurrentCity
      ? this.customerListForm.get('city')?.value
      : '';
    this.availableCities = withExistingCityOption(
      getUsaCitiesForState(stateValue),
      currentCity
    );
    this.updateLocationFieldStates();
  }

  private updateLocationFieldStates(): void {
    const stateCtrl = this.customerListForm?.get('state');
    const cityCtrl = this.customerListForm?.get('city');
    if (!stateCtrl || !cityCtrl) {
      return;
    }

    if (this.isUsaCountrySelected) {
      stateCtrl.enable({ emitEvent: false });
    } else {
      stateCtrl.disable({ emitEvent: false });
    }

    if (this.isUsaCountrySelected && this.isStateSelected) {
      cityCtrl.enable({ emitEvent: false });
    } else {
      cityCtrl.disable({ emitEvent: false });
    }
  }

  private applyUserEmailFieldRules(): void {
    const ctrl = this.customerListForm?.get('contactEmail');
    if (!ctrl) {
      return;
    }
    ctrl.enable({ emitEvent: false });
    ctrl.setValidators(getBusinessEmailValidators({ required: true }));
    ctrl.updateValueAndValidity({ emitEvent: false });
  }



  get wiliotEnabled(): boolean {

    return !!this.customerListForm?.get('wiliotEnabled')?.value;

  }



  toggleWiliot(): void {

    const ctrl = this.customerListForm.get('wiliotEnabled');

    ctrl?.setValue(!ctrl.value);

  }

  openWiliotGatewayTokenPopover(): void {
    this.showWiliotGatewayTokenPopover = true;
  }

  closeWiliotGatewayTokenPopover(): void {
    this.showWiliotGatewayTokenPopover = false;
  }

  setCloudValues(provider: string): void {

    if (provider === 'GCP') {

      this.customerListForm.patchValue({

        wiliotCloud: 'ssl://mqtt.us-central1.prod.gcp.wiliot.cloud:8883',

        targetUrl: 'https://api.us-central1.prod.gcp.wiliot.cloud/v1',

      });

    } else if (provider === 'AWS') {

      this.customerListForm.patchValue({

        wiliotCloud: 'ssl://mqtt.us-east-2.prod.wiliot.cloud:8883',

        targetUrl: 'https://api.us-east-2.prod.wiliot.cloud/v1',

      });

    } else {

      this.customerListForm.patchValue({ wiliotCloud: '', targetUrl: '' });

    }

  }



  onSubmit(): void {
    if (this.isSubmitting || this.isSubmitBlockedForResellerParent) {
      return;
    }

    if (this.customerListForm.invalid) {
      this.customerListForm.markAllAsTouched();
      // Allow *ngIf error messages / .ng-touched classes to render, then focus first invalid.
      setTimeout(() => this.focusFirstInvalidControl());
      return;
    }

    this.isAddMode ? this.addCustomer() : this.updateCustomer();
  }

  /** Scroll/focus the first invalid control in DOM order (generic — no field list). */
  private focusFirstInvalidControl(): void {
    const root = this.host.nativeElement;
    const firstInvalid = root.querySelector(
      'form input.ng-invalid, form select.ng-invalid, form textarea.ng-invalid'
    ) as HTMLElement | null;

    if (!firstInvalid) {
      return;
    }

    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof firstInvalid.focus === 'function') {
      firstInvalid.focus({ preventScroll: true });
    }
  }



  private bindCustomerTypeChanges(): void {
    if (this.isResellerCustomerLocked) {
      return;
    }
    this.customerListForm
      .get('customerTypeId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((typeId) => {
        this.updateParentCustomerValidators(typeId);
        if (this.isCustomerTypeId(typeId)) {
          this.enableParentOrganizationSelection();
          this.loadResellers();
        } else if (this.isResellerTypeId(typeId)) {
          this.applyMontageParentOrganizationRules();
        } else {
          this.customerListForm.patchValue({ parentResellerId: null }, { emitEvent: false });
          this.enableParentOrganizationSelection();
        }
      });
  }

  private applyResellerCustomerRules(): void {
    const customerTypeId = this.getDefaultCustomerTypeId();
    if (customerTypeId == null) {
      return;
    }

    this.lockedParentResellerId = null;
    this.isResellerParentReady = false;
    this.customerListForm.patchValue({ customerTypeId }, { emitEvent: false });
    this.customerListForm.get('customerTypeId')?.disable({ emitEvent: false });
    this.updateParentCustomerValidators(customerTypeId);
    this.loadLoggedInResellerParent();
  }

  /** Edit mode: keep the record's customer type; field stays read-only for reseller users. */
  private applyResellerEditModeRules(customerTypeId: number | null): void {
    this.customerListForm.get('customerTypeId')?.disable({ emitEvent: false });
    this.updateParentCustomerValidators(customerTypeId);
    if (this.isCustomerTypeId(customerTypeId)) {
      this.loadLoggedInResellerParent();
    }
  }

  private resolveCurrentCustomerTypeId(): number | null {
    const fromForm = this.customerListForm?.get('customerTypeId')?.value;
    if (fromForm != null && fromForm !== '') {
      return Number(fromForm);
    }
    return this.resolveCustomerTypeId(
      this.flattenCustomerTypeFields(this.data?.rowData ?? {})
    );
  }

  private resolveCustomerTypeId(row: any): number | null {
    const flat = this.flattenCustomerTypeFields(row);

    const explicitId =
      flat?.customerTypeId ?? flat?.typeId ?? flat?.customerTypeRefId;

    if (explicitId != null && explicitId !== '') {
      const numericId = Number(explicitId);
      if (
        Number.isFinite(numericId) &&
        numericId > 0 &&
        (!this.customerTypes.length ||
          this.customerTypes.some((t) => Number(t.typeId) === numericId))
      ) {
        return numericId;
      }
    }

    const labelCandidates = [
      typeof flat?.customerType === 'string' ? flat.customerType : null,
      flat?.customerTypeName,
      flat?.customerTypeLabel,
      flat?.typeName,
      flat?.typeCode,
    ].filter((value) => value != null && String(value).trim() !== '');

    for (const label of labelCandidates) {
      const match = this.matchCustomerTypeByLabel(String(label));
      if (match) {
        return match.typeId;
      }
    }

    if (this.isEditMode) {
      return this.inferCustomerTypeIdForEdit(flat);
    }

    return this.getDefaultCustomerTypeId();
  }

  private matchCustomerTypeByLabel(label: string): CustomerTypeOption | undefined {
    const trimmed = label.trim();
    if (!trimmed) {
      return undefined;
    }
    const upper = trimmed.toUpperCase();
    return this.customerTypes.find(
      (t) =>
        t.label === trimmed ||
        t.typeCode === upper ||
        t.label.toUpperCase() === upper
    );
  }

  private matchCustomerTypeByCode(code: string): CustomerTypeOption | undefined {
    return this.customerTypes.find((t) => t.typeCode === code);
  }

  /** When BE omits customerTypeId in edit, infer from parent-customer relationship. */
  private inferCustomerTypeIdForEdit(row: any): number | null {
    const parentId =
      row?.parentResellerId ??
      row?.parentCustomerId ??
      row?.parentId ??
      row?.resellerId ??
      row?.parentCustomer?.id ??
      row?.parentCustomer?.customerId;

    if (parentId != null && parentId !== '' && Number(parentId) > 0) {
      return (
        this.matchCustomerTypeByCode('CUSTOMER')?.typeId ?? CUSTOMER_TYPE_ID_CUSTOMER
      );
    }

    return (
      this.matchCustomerTypeByCode('RESELLER')?.typeId ?? CUSTOMER_TYPE_ID_RESELLER
    );
  }

  private loadLoggedInResellerParent(): void {
    if (this.isLoadingResellers) {
      return;
    }
    this.isLoadingResellers = true;
    this.isResellerParentReady = false;
    this.customerService.getResellerCustomers().subscribe({
      next: (list) => {
        const parentId = this.resolveResellerParentIdFromContext(list);
        if (parentId != null) {
          const info = this.buildLockedResellerInfo(parentId, list);
          if (info) {
            this.applyLockedParentReseller(info);
            this.isLoadingResellers = false;
            return;
          }
        }
        this.loadResellerParentFromAdministeredIds();
      },
      error: () => {
        this.isLoadingResellers = false;
        this.loadResellerParentFromAdministeredIds();
        this.toastr.warning(
          'Failed to load parent customer for your reseller account.',
          'Load Warning',
          { timeOut: 5000, closeButton: true, progressBar: true }
        );
      },
    });
  }

  private resolveResellerParentIdFromContext(
    resellers: CustomerV1ListItem[]
  ): number | null {
    const fromMatch = this.rbacService.getLoggedInResellerInfo(resellers);
    if (fromMatch?.id != null && fromMatch.id > 0) {
      return fromMatch.id;
    }

    const administered = this.rbacService.getAdministeredCustomerIds();
    for (const adminId of administered) {
      const adminStr = String(adminId);
      const reseller = resellers.find((row) =>
        [row.id, row.customerId].some(
          (value) => value != null && String(value) === adminStr
        )
      );
      const resolved = reseller ? resolveResellerRecordId(reseller) : null;
      if (resolved != null) {
        return resolved;
      }

      const numeric = Number(adminId);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }

    return null;
  }

  private buildLockedResellerInfo(
    parentId: number,
    resellers: CustomerV1ListItem[]
  ): { id: number; customerName: string; accountNumber?: string } | null {
    const match =
      resellers.find((row) => resolveResellerRecordId(row) === parentId) ??
      resellers.find((row) =>
        [row.id, row.customerId].some(
          (value) => value != null && Number(value) === parentId
        )
      );

    return {
      id: parentId,
      customerName: String(match?.customerName ?? 'Reseller').trim(),
      accountNumber: match?.accountNumber
        ? String(match.accountNumber)
        : undefined,
    };
  }

  private pickPositiveId(candidates: unknown[]): number | null {
    for (const candidate of candidates) {
      if (candidate == null || candidate === '') {
        continue;
      }
      const numeric = Number(candidate);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }
    return null;
  }

  private applyLockedParentReseller(info: {
    id: number;
    customerName: string;
    accountNumber?: string;
  }): void {
    if (!Number.isFinite(info.id) || info.id <= 0) {
      this.isResellerParentReady = false;
      return;
    }
    this.lockedParentResellerId = info.id;
    this.isResellerParentReady = true;
    this.resellers = [
      {
        id: info.id,
        customerId: info.id,
        customerName: info.customerName,
        accountNumber: info.accountNumber,
      },
    ];
    this.lockedParentResellerLabel = this.formatResellerLabel(info);
    this.customerListForm.patchValue({ parentResellerId: info.id }, { emitEvent: false });
    this.customerListForm.get('parentResellerId')?.disable({ emitEvent: false });
  }

  private loadResellerParentFromAdministeredIds(): void {
    const id = this.pickPositiveId(this.rbacService.getAdministeredCustomerIds());
    if (id == null) {
      this.isLoadingResellers = false;
      this.isResellerParentReady = false;
      return;
    }

    this.customerService.getCustomerById(id).subscribe({
      next: (response) => {
        const row = this.normalizeCustomerRecord(response);
        const parentId =
          this.pickPositiveId([
            resolveResellerRecordId(row),
            this.resolveCustomerRecordId(row),
            row?.id,
            row?.customerId,
            id,
          ]) ?? id;
        const info = {
          id: parentId,
          customerName: String(row.customerName ?? 'Reseller').trim(),
          accountNumber: row.accountNumber ? String(row.accountNumber) : undefined,
        };
        this.applyLockedParentReseller(info);
        this.isLoadingResellers = false;
      },
      error: () => {
        this.isLoadingResellers = false;
        this.isResellerParentReady = false;
      },
    });
  }

  private formatResellerLabel(info: {
    customerName: string;
    accountNumber?: string;
  }): string {
    const name = info.customerName?.trim() ?? '';
    const account = info.accountNumber?.trim();
    if (name && account) {
      return `${name} (${account})`;
    }
    return name || account || 'Reseller';
  }

  private isCustomerTypeId(typeId: number | string | null | undefined): boolean {
    if (typeId == null || typeId === '') {
      return false;
    }
    const numericId = Number(typeId);
    const match = this.customerTypes.find((t) => t.typeId === numericId);
    if (match) {
      return (
        match.typeCode === 'CUSTOMER' ||
        match.label === 'Customer' ||
        match.typeId === CUSTOMER_TYPE_ID_CUSTOMER
      );
    }
    return numericId === CUSTOMER_TYPE_ID_CUSTOMER;
  }

  private isResellerTypeId(typeId: number | string | null | undefined): boolean {
    if (typeId == null || typeId === '') {
      return false;
    }
    const numericId = Number(typeId);
    const match = this.customerTypes.find((t) => t.typeId === numericId);
    if (match) {
      return (
        match.typeCode === 'RESELLER' ||
        match.label === 'Reseller' ||
        match.typeId === CUSTOMER_TYPE_ID_RESELLER
      );
    }
    return numericId === CUSTOMER_TYPE_ID_RESELLER;
  }

  private updateParentCustomerValidators(typeId: number | string | null | undefined): void {
    const ctrl = this.customerListForm.get('parentResellerId');
    if (!ctrl) {
      return;
    }
    if ((this.isCustomerTypeId(typeId) || this.isResellerTypeId(typeId)) && this.isAddMode) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      if (!this.isCustomerTypeId(typeId) && !this.isResellerTypeId(typeId)) {
        ctrl.setValue(null, { emitEvent: false });
        this.enableParentOrganizationSelection();
      }
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private enableParentOrganizationSelection(): void {
    if (this.isResellerCustomerLocked || this.isMontageParentLocked) {
      return;
    }
    this.montageParentOrganizationId = null;
    this.montageParentDisplayLabel = '';
    this.customerListForm.get('parentResellerId')?.enable({ emitEvent: false });
  }

  private findMontageOrganization(
    list: CustomerV1ListItem[]
  ): CustomerV1ListItem | undefined {
    const target =
      EditCustomerListComponent.MONTAGE_PARENT_ORGANIZATION_NAME.toLowerCase();
    return list.find((row) => {
      const name = String(row.customerName ?? '').trim().toLowerCase();
      return (
        name === target ||
        name.startsWith(`${target} `) ||
        name.startsWith(`${target}(`)
      );
    });
  }

  /** Reseller customer type always links to Montage as the parent organization. */
  private applyMontageParentOrganizationRules(): void {
    if (this.isResellerCustomerLocked) {
      return;
    }

    if (this.montageParentOrganizationId != null) {
      this.applyMontageParentLocked({
        id: this.montageParentOrganizationId,
        customerName:
          this.montageParentDisplayLabel ||
          EditCustomerListComponent.MONTAGE_PARENT_ORGANIZATION_NAME,
      });
      return;
    }

    if (this.isLoadingResellers) {
      return;
    }

    this.isLoadingResellers = true;
    this.customerService.getResellerCustomers().subscribe({
      next: (list) => {
        const montage = this.findMontageOrganization(list);
        const id = montage ? resolveResellerRecordId(montage) : null;
        if (id != null && montage) {
          this.applyMontageParentLocked({
            id,
            customerName: String(
              montage.customerName ??
                EditCustomerListComponent.MONTAGE_PARENT_ORGANIZATION_NAME
            ).trim(),
            accountNumber: montage.accountNumber
              ? String(montage.accountNumber)
              : undefined,
          });
        } else {
          this.montageParentDisplayLabel =
            EditCustomerListComponent.MONTAGE_PARENT_ORGANIZATION_NAME;
          this.customerListForm.get('parentResellerId')?.disable({ emitEvent: false });
        }
        this.isLoadingResellers = false;
      },
      error: () => {
        this.isLoadingResellers = false;
        this.montageParentDisplayLabel =
          EditCustomerListComponent.MONTAGE_PARENT_ORGANIZATION_NAME;
        this.customerListForm.get('parentResellerId')?.disable({ emitEvent: false });
        this.toastr.warning(
          'Failed to load Montage parent organization.',
          'Load Warning',
          { timeOut: 5000, closeButton: true, progressBar: true }
        );
      },
    });
  }

  private applyMontageParentLocked(info: {
    id: number;
    customerName: string;
    accountNumber?: string;
  }): void {
    if (!Number.isFinite(info.id) || info.id <= 0) {
      return;
    }
    this.montageParentOrganizationId = info.id;
    this.montageParentDisplayLabel =
      this.formatResellerLabel(info) ||
      EditCustomerListComponent.MONTAGE_PARENT_ORGANIZATION_NAME;
    this.customerListForm.patchValue({ parentResellerId: info.id }, { emitEvent: false });
    this.customerListForm.get('parentResellerId')?.disable({ emitEvent: false });
  }

  private loadResellers(): void {
    if (this.isResellerCustomerLocked || this.resellers.length > 0 || this.isLoadingResellers) {
      return;
    }
    this.isLoadingResellers = true;
    this.customerService.getResellerCustomers().subscribe({
      next: (list) => {
        this.resellers = list;
        this.isLoadingResellers = false;
      },
      error: () => {
        this.isLoadingResellers = false;
        this.toastr.warning(
          'Failed to load reseller list for Parent Customer.',
          'Load Warning',
          { timeOut: 5000, closeButton: true, progressBar: true }
        );
      },
    });
  }

  /** Default Add Customer type: Customer (typeId 2), not Reseller (typeId 1). */
  private getDefaultCustomerTypeId(): number | null {
    const fromApi =
      this.customerTypes.find((t) => t.typeCode === 'CUSTOMER') ??
      this.customerTypes.find((t) => t.label === 'Customer');
    if (fromApi) {
      return fromApi.typeId;
    }

    if (this.customerTypes.some((t) => t.typeId === CUSTOMER_TYPE_ID_CUSTOMER)) {
      return CUSTOMER_TYPE_ID_CUSTOMER;
    }

    return null;
  }

  private sortCustomerTypes(types: CustomerTypeOption[]): CustomerTypeOption[] {
    const priority = [CUSTOMER_TYPE_ID_CUSTOMER, CUSTOMER_TYPE_ID_RESELLER];
    return [...types].sort((a, b) => {
      const ai = priority.indexOf(a.typeId);
      const bi = priority.indexOf(b.typeId);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.typeId - b.typeId;
    });
  }



  private resolveIsActive(row: any): boolean {

    const status = row?.status;

    if (typeof status === 'string') {

      return status.trim().toUpperCase() === 'ACTIVE';

    }

    return row?.isActive ?? true;

  }



  private buildApiPayload(raw: any): CreateCustomerPayload {

    // Create flow always persists Active; edit continues to use the form value.
    const status: CustomerStatus = this.isAddMode
      ? 'ACTIVE'
      : raw.isActive
        ? 'ACTIVE'
        : 'INACTIVE';



    const payload: CreateCustomerPayload = {
      customerName: raw.customerName?.trim() ?? '',
      accountNumber: raw.accountNumber?.trim() ?? '',
      contactEmail: raw.contactEmail?.trim() ?? '',
      contactFirstName: raw.contactFirstName?.trim() ?? '',
      contactLastName: raw.contactLastName?.trim() ?? '',
      phoneNumber: raw.phoneNumber?.trim() ?? '',
      city: raw.city?.trim() || undefined,
      state: raw.state?.trim() || undefined,
      zip: raw.zip?.trim() || undefined,
      country: raw.country?.trim() || undefined,
      address: raw.address?.trim() || undefined,
      status,
      customerTypeId: Number(raw.customerTypeId),
      cloudProvider: raw.cloudProvider || '',
      wiliotEnabled: raw.wiliotEnabled ?? false,
      wiliotGatewayNameFormat:
        String(raw.wiliotGatewayNameFormat ?? '').trim() || null,
    };

    if (this.isCustomerTypeId(raw.customerTypeId)) {
      const parentResellerId = this.resolveParentResellerIdForCreate(raw);
      if (parentResellerId != null) {
        payload.parentResellerId = parentResellerId;
      }
    } else if (this.isResellerTypeId(raw.customerTypeId)) {
      const parentResellerId = this.resolveMontageParentIdForCreate(raw);
      if (parentResellerId != null) {
        payload.parentResellerId = parentResellerId;
      }
    }

    return payload;
  }

  private resolveMontageParentIdForCreate(
    raw: Record<string, unknown>
  ): number | undefined {
    return (
      this.pickPositiveId([
        this.montageParentOrganizationId,
        raw['parentResellerId'],
      ]) ?? undefined
    );
  }

  /** Reseller child customers must link to a valid parent id (> 0). */
  private resolveParentResellerIdForCreate(raw: Record<string, unknown>): number | undefined {
    const candidates: unknown[] = [
      this.lockedParentResellerId,
      raw['parentResellerId'],
    ];

    for (const reseller of this.resellers) {
      candidates.push(resolveResellerRecordId(reseller));
    }

    const administered = this.rbacService.getAdministeredCustomerIds();
    for (const adminId of administered) {
      const adminStr = String(adminId);
      const match = this.resellers.find((row) =>
        [row.id, row.customerId].some(
          (value) => value != null && String(value) === adminStr
        )
      );
      if (match) {
        candidates.push(resolveResellerRecordId(match));
      }
      candidates.push(adminId);
    }

    return this.pickPositiveId(candidates) ?? undefined;
  }



  private resolveToastMessage(
    response: { message?: string } | null | undefined,
    fallback: string
  ): string {
    const msg = response?.message?.trim();
    if (!msg || msg.toLowerCase() === 'success') {
      return fallback;
    }
    return msg;
  }

  /** Prefer validation `details` from 400 responses (e.g. duplicate account number). */
  private resolveApiErrorMessage(error: unknown, fallback: string): string {
    return buildApiErrorMessage(error, {
      fallback,
      statusFallbacks: CUSTOMER_CREATE_STATUS_FALLBACKS,
      duplicateEntryMessage: CUSTOMER_CREATE_DUPLICATE_MESSAGE,
    });
  }

  private addCustomer(): void {
    const raw = this.customerListForm.getRawValue();
    const payload = this.buildApiPayload(raw);

    if (
      this.isResellerCustomerLocked &&
      this.isCustomerTypeId(payload.customerTypeId) &&
      payload.parentResellerId == null
    ) {
      this.toastr.error(
        'Unable to determine parent customer for your reseller account. Please refresh and try again.',
        EditCustomerListComponent.CREATE_CUSTOMER_FAILED_TITLE,
        { timeOut: 6000, closeButton: true, progressBar: true }
      );
      return;
    }

    if (
      this.isResellerTypeId(payload.customerTypeId) &&
      payload.parentResellerId == null
    ) {
      this.toastr.error(
        'Unable to determine Montage parent organization. Please refresh and try again.',
        EditCustomerListComponent.CREATE_CUSTOMER_FAILED_TITLE,
        { timeOut: 6000, closeButton: true, progressBar: true }
      );
      return;
    }

    this.isSubmitting = true;
    this.customerService.createCustomer(payload).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        const name = payload.customerName || 'Customer';
        this.toastr.success(
          this.resolveToastMessage(
            response,
            `Customer "${name}" has been added successfully.`
          ),
          'Customer Added',
          { timeOut: 4000, closeButton: true, progressBar: true }
        );
        clearFormUnsavedChanges(
          this.customerListForm,
          CUSTOMER_FORM_DIRTY_SOURCE,
          this.unsavedChanges
        );
        this.dialogRef.close({ success: true });
      },
      error: (error: unknown) => {
        this.isSubmitting = false;
        const msg = this.resolveApiErrorMessage(
          error,
          'Failed to create customer. Please try again.'
        );
        this.toastr.error(msg, EditCustomerListComponent.CREATE_CUSTOMER_FAILED_TITLE, {
          timeOut: 6000,
          closeButton: true,
          progressBar: true,
        });
      },
    });
  }



  private updateCustomer(): void {
    const raw = this.customerListForm.getRawValue();
    const recordId =
      this.editRecordId ?? this.resolveCustomerRecordId(raw as Record<string, unknown>);

    if (recordId == null) {
      this.toastr.error('Missing customer id — cannot update this customer.', 'Update Failed', {
        timeOut: 5000,
        closeButton: true,
        progressBar: true,
      });
      return;
    }

    const payload: UpdateCustomerPayload = {
      id: recordId,
      ...this.buildApiPayload(raw),
    };

    this.isSubmitting = true;

    this.customerService.updateCustomerOnly(payload, recordId).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        const name = payload.customerName || 'Customer';
        this.toastr.success(
          this.resolveToastMessage(
            response,
            `Customer "${name}" has been updated successfully.`
          ),
          'Customer Updated',
          { timeOut: 4000, closeButton: true, progressBar: true }
        );
        clearFormUnsavedChanges(
          this.customerListForm,
          CUSTOMER_FORM_DIRTY_SOURCE,
          this.unsavedChanges
        );
        this.dialogRef.close({ success: true });
      },
      error: (error: unknown) => {
        this.isSubmitting = false;
        const msg = this.resolveApiErrorMessage(
          error,
          'Failed to update customer. Please try again.'
        );
        this.toastr.error(msg, 'Update Failed', {
          timeOut: 6000,
          closeButton: true,
          progressBar: true,
        });
      },
    });

  }

}


